import React, { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import _, {clone} from 'lodash';

import { SwButtonSection, getFetcher } from '../../../../App';
import { Input, PrimaryButton, Sub1Button, Switch } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import { useCatchLeave } from '../../../../utils/hooks';

import { submitDebounce } from '../../../../utils/lib';
import { StyledReportArea, StyledReportFlex, StyledSendItemFlex, StyledSeriesConfigFlex, StyledSeriesList } from './style';
import { StyledReportWrap } from '../Option/style';

interface sendToPacsState {
  [key: string]: any;
}

const defaultValue = {
  sendResult: null,
  seriesCompositionType: null,
  sendItemVesselDensityVertical: null,
  sendItemVesselDensityHorizontal: null,
  sendItemNeurovascularAnatomy: null,
  sendItem3DViewVertical: null,
  sendItem3DViewHorizontal: null,
};

const Setting = () => {
  const [isChanged, setIsChanged] = useState(false);
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const { errorHandler } = SettingCommonMethods();
  const [data, setData] = useState<sendToPacsState>({ ...defaultValue });
  const [backupData, setBackupData] = useState<sendToPacsState>({ ...defaultValue });

  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(isChanged);

  /**
   * setting 데이터 가져오기
   */
  const getSettingData = async () => {
    const url = 'api/setting/cta/pacs';
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setData({ ...data, ...r.result });
          setBackupData({ ...backupData, ...r.result });
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * 변경값 저장 하기
   */
  const postReportSetting = async () => {
    // valid 체크
    const url = `api/setting/cta/pacs`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const _body = {
      ...data
    };

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _body,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData({...data});
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * report 사용여부 변경 전 체크
   */
  const isChangeSetting = async (e: boolean) => {
    setDataValue('sendResult', e);

    const isOn = e;

    setDialogContents({
      type: 'plain',
      body: `Are you sure you want to turn ${
        isOn ? 'on' : 'off'
      } automatically image send? \n\r(You must click Apply for the changes to take effect.)`,
      footer: (
        <>
          <Sub1Button
            onClick={() => {
              setDataValue('sendResult', !isOn);
              setDialogContents({ type: 'hidden' });
            }}
          >
            {`No, I don't`}
          </Sub1Button>

          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
              setDataValue('sendResult', e);
            }}
          >
            Yes, turn {isOn ? 'on' : 'off'}
          </PrimaryButton>
        </>
      ),
    });
  };

  const setDataValue = (key: string, value: boolean) => {
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });
  };

  /**
   * series Config 변경 핸들러
   */
  const seriesConfigHandler = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const modeValue = ev.target.value;
      const modeName = ev.target.name;

      setData((prevState) => {
        if (!prevState) return null;
        const cloneData = JSON.parse(JSON.stringify(prevState));
        cloneData.seriesCompositionType = backupData?.seriesCompositionType;
        cloneData[modeName] = modeValue;

        return cloneData;
      });
    },
    [backupData],
  );

  /**
   * 최초 데이터 설정
   */
  useEffect(() => {
    getSettingData();
  }, []);

  useEffect(() => {
    // 값이 바꼈을 때 true
    // 유효성검사 통과 되었을 때 true

    const isSameValue = _.isEqual(data, backupData);
    setIsChanged(isSameValue); // true:
  }, [data, backupData]);

  return (
    <>
      <label>Send to PACS</label>

      <StyledReportWrap>
        <StyledReportArea>
          <strong>Send automatically</strong>
          <StyledReportFlex>
            <span className={'send-off'}>Off</span>
            <Switch
              // key={switchIndex}
              defaultValue={data.sendResult}
              onChangeValue={(e) => isChangeSetting(e)}
            />
            <span>On</span>
          </StyledReportFlex>
        </StyledReportArea>
      </StyledReportWrap>

      <StyledReportWrap>
        <StyledReportArea>
          <strong>Send items</strong>
          <StyledSendItemFlex>
            <li>
              <Input
                type={'checkbox'}
                name={'sendItemVesselDensityVertical'}
                checked={data.sendItemVesselDensityVertical}
                onChange={(e) => setDataValue('sendItemVesselDensityVertical', e.target.checked)}
              >
                VesselDensity-Vertical
              </Input>
            </li>
            <li>
              <Input
                type={'checkbox'}
                name={'sendItemVesselDensityHorizontal'}
                checked={data.sendItemVesselDensityHorizontal}
                onChange={(e) => setDataValue('sendItemVesselDensityHorizontal', e.target.checked)}
              >
                VesselDensity-Horizontal
              </Input>
            </li>
            <li>
              <Input
                type={'checkbox'}
                name={'sendItemNeurovascularAnatomy'}
                checked={data.sendItemNeurovascularAnatomy}
                onChange={(e) => setDataValue('sendItemNeurovascularAnatomy', e.target.checked)}
              >
                NeurovascularAnatomy
              </Input>
            </li>
            <li>
              <Input
                type={'checkbox'}
                name={'sendItem3DViewVertical'}
                checked={data.sendItem3DViewVertical}
                onChange={(e) => setDataValue('sendItem3DViewVertical', e.target.checked)}
              >
                3D View-Vertical
              </Input>
            </li>
            <li>
              <Input
                type={'checkbox'}
                name={'sendItem3DViewHorizontal'}
                checked={data.sendItem3DViewHorizontal}
                onChange={(e) => setDataValue('sendItem3DViewHorizontal', e.target.checked)}
              >
                3D View-Horizontal
              </Input>
            </li>
          </StyledSendItemFlex>
        </StyledReportArea>
      </StyledReportWrap>

      <StyledReportWrap>
        <StyledReportArea>
          <strong>Series config (3D view)</strong>
          <StyledSeriesConfigFlex>
            <StyledSeriesList>
              <li>
                <Input
                  type="radio"
                  name="seriesCompositionType"
                  value="INDIVIDUAL"
                  checked={data.seriesCompositionType === 'INDIVIDUAL'}
                  onChange={seriesConfigHandler}
                >
                  Individual
                </Input>
              </li>
              <li>
                <Input
                  type="radio"
                  name="seriesCompositionType"
                  value="INTEGRATION"
                  checked={data.seriesCompositionType === 'INTEGRATION'}
                  onChange={seriesConfigHandler}
                >
                  Integration
                </Input>
              </li>
            </StyledSeriesList>
          </StyledSeriesConfigFlex>
        </StyledReportArea>
      </StyledReportWrap>

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(postReportSetting)}
          disabled={isChanged}
        >
          Apply
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default Setting;
